import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  SearchOutlined,
  GridView as CardViewIcon,
  ViewStreamOutlined as CompactViewIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Offer, OfferSortParameter, OfferStatusCode, OffersState } from "../models/models";
import { createOfferStatusLabelMap } from "../utils/offerStatusMaps";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  getOffers,
  changeOffersQuery,
  getOffersFilters,
  submitOffer,
  withdrawOffer,
  deleteOffer,
  confirmOffer,
  startWork,
  markWorkComplete,
  archiveOffer,
  unarchiveOffer,
  clearError
} from "../customerOffersSlice";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import Loader from "../../../shared/components/Loader";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import LightTablePagination from "../../../shared/components/tables/LightTablePagination";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import ConfirmServiceDialog from "./ConfirmServiceDialog";
import CompleteWorkDialog from "./CompleteWorkDialog";
import ActionRequiredStrip from "./ActionRequiredStrip";
import OfferCard from "./OfferCard";
import OfferCardCompact from "./OfferCardCompact";
import ActionToast from "../../../shared/components/ActionToast";
import { NotificationMessage } from "../../../shared/models/models";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { dt, paginationWrapperSx, StatusBadgeColorConfig } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import RedesignStatusBadge from "../../../shared/components/redesign/RedesignStatusBadge";
import RedesignFilterItem from "../../../shared/components/redesign/RedesignFilterItem";
import useDataRefresh from "../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../shared/hooks/useVisibilityRefresh";
import useChangeHighlight from "../../../shared/hooks/useChangeHighlight";
import ServiceLifecycleTracker from "../../shared/components/ServiceLifecycleTracker";
import ArchiveViewToggle from "../../shared/components/ArchiveViewToggle";
import { getProviderLifecycleStages } from "../../shared/utils/lifecycleStages";

// ═══════════════════════════════════════════════════════════════
//  STATUS COLORS
// ═══════════════════════════════════════════════════════════════

const statusBadgeColors: Record<number, StatusBadgeColorConfig> = {
  [OfferStatusCode.draft]: { bg: "#F5EEF8", text: "#8E44AD", border: "#D2B4DE" },
  [OfferStatusCode.sent]: { bg: "#EBF5FB", text: dt.blueHover, border: "#AED6F1" },
  [OfferStatusCode.viewed]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
  [OfferStatusCode.negotiating]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [OfferStatusCode.accepted]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [OfferStatusCode.rejected]: { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" },
  [OfferStatusCode.expired]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
  [OfferStatusCode.withdrawn]: { bg: "#F4F6F9", text: "#9CA3AF", border: "#D1D5DB" },
  [OfferStatusCode.awaitingConfirmation]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [OfferStatusCode.confirmed]: { bg: "#E8F8F5", text: "#1ABC9C", border: "#A3E4D7" },
  [OfferStatusCode.workInProgress]: { bg: "#EBF5FB", text: dt.blueHover, border: "#AED6F1" },
  [OfferStatusCode.workCompleted]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [OfferStatusCode.serviceCompleted]: { bg: "#D5F5E3", text: "#1E8449", border: "#82E0AA" },
  [OfferStatusCode.archived]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
};


// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════

const allActiveStatusCodes = [
  OfferStatusCode.sent,
  OfferStatusCode.viewed,
  OfferStatusCode.negotiating,
  OfferStatusCode.awaitingConfirmation,
  OfferStatusCode.confirmed,
  OfferStatusCode.workInProgress,
];

const allClosedStatusCodes = [
  OfferStatusCode.accepted,
  OfferStatusCode.workCompleted,
  OfferStatusCode.serviceCompleted,
  OfferStatusCode.rejected,
  OfferStatusCode.expired,
  OfferStatusCode.withdrawn,
];


// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const MyOffers = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const { offersQuery, offers, offersFilters, loading, error } =
    useAppSelector<OffersState>((state: any) => state.customerOffers);
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();

  // 3-tier responsive: Desktop > 1200, Tablet 768–1200, Phone < 768
  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState<string>(offersQuery.search || "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmingOffer, setConfirmingOffer] = useState<Offer | null>(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completingOffer, setCompletingOffer] = useState<Offer | null>(null);
  const [completeLoading, setCompleteLoading] = useState(false);

  // Card view toggle
  const [viewMode, setViewMode] = useState<"cards" | "compact">("cards");

  // Archive view toggle: "active" shows all non-archived, "archived" shows only archived
  const [archiveViewMode, setArchiveViewMode] = useState<"active" | "archived">("active");

  const handleCardClick = useCallback((item: Offer) => {
    navigate(`/service-offers/${item.id}`);
  }, [navigate]);

  useRedesignSetup();

  // Change highlight: track which offers changed on silent refresh
  const changedIds = useChangeHighlight(
    offers?.items ?? [],
    (item) => item.id,
    (item) => `${item.statusCode}-${item.totalEstimatedCost}`,
  );

  // Real-time: silently refresh offers when DataRefresh arrives
  useDataRefresh("myOffers", () => {
    dispatch(getOffers(offersQuery));
    dispatch(getOffersFilters({ search: offersQuery.search }));
  });
  useVisibilityRefresh(() => {
    dispatch(getOffers(offersQuery));
    dispatch(getOffersFilters({ search: offersQuery.search }));
  });

  // --- Status label map ---
  const statusLabelMap = useMemo(() => createOfferStatusLabelMap(translate), [translate]);

  // --- Filter data ---
  const { activeFilters, closedFilters, archiveFilter } = useMemo(() => {
    const filters = offersFilters ?? { statusCodes: [] };
    const activeFilters = allActiveStatusCodes.map(statusCode => {
      const existingFilter = filters.statusCodes.find(f => f.id === statusCode);
      return existingFilter || { id: statusCode, name: statusLabelMap[statusCode], value: 0 };
    });
    const closedFilters = allClosedStatusCodes.map(statusCode => {
      const existingFilter = filters.statusCodes.find(f => f.id === statusCode);
      return existingFilter || { id: statusCode, name: statusLabelMap[statusCode], value: 0 };
    });
    const archiveFilter = filters.statusCodes.find(f => f.id === OfferStatusCode.archived) || {
      id: OfferStatusCode.archived,
      name: statusLabelMap[OfferStatusCode.archived],
      value: 0,
    };
    return { activeFilters, closedFilters, archiveFilter };
  }, [offersFilters, statusLabelMap]);

  const activeTotal = useMemo(
    () => activeFilters.reduce((sum, f) => sum + f.value, 0),
    [activeFilters]
  );

  const closedTotal = useMemo(
    () => closedFilters.reduce((sum, f) => sum + f.value, 0),
    [closedFilters]
  );

  const allNonArchivedTotal = activeTotal + closedTotal;

  // ═══════════════════════════════════════════════════════════
  //  EVENT HANDLERS — unchanged
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    const query = {
      ...offersQuery,
      statusCodes: [...allActiveStatusCodes, ...allClosedStatusCodes], // Exclude archived by default
      pageNo: 1,
      sortBy: OfferSortParameter.createdDate,
      sortDesc: true
    };
    dispatch(changeOffersQuery(query));
    dispatch(getOffers(query));
    dispatch(getOffersFilters({ search: query.search }));
  }, []);

  const handleSort = (parameter: number) => {
    const currentSortBy = offersQuery.sortBy;
    const currentSortDesc = offersQuery.sortDesc ?? false;
    const sortDesc = currentSortBy !== parameter ? false : !currentSortDesc;
    const newQuery = {
      ...offersQuery,
      sortBy: parameter as OfferSortParameter,
      sortDesc: sortDesc,
      pageNo: 1
    };

    dispatch(changeOffersQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const newQuery = { ...offersQuery, pageNo: 1, pageSize: newPageSize };
    dispatch(changeOffersQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handlePageNoChange = (newPage: number) => {
    const newQuery = { ...offersQuery, pageNo: newPage };
    dispatch(changeOffersQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handleSearch = (value?: string) => {
    const newQuery = { ...offersQuery, search: value, pageNo: 1 };
    dispatch(changeOffersQuery(newQuery));
    dispatch(getOffers(newQuery));
    dispatch(getOffersFilters({ search: value }));
  };

  const handleFiltersChange = (selectedStatusCodes: number[]) => {
    // When computing the API query, always respect archive view mode:
    // - In "active" mode: ensure archived is excluded
    // - In "archived" mode: only query archived
    const effectiveCodes = archiveViewMode === "archived"
      ? [OfferStatusCode.archived]
      : selectedStatusCodes.length > 0
        ? selectedStatusCodes.filter(c => c !== OfferStatusCode.archived)
        : [...allActiveStatusCodes, ...allClosedStatusCodes];  // show all non-archived
    const newQuery = {
      ...offersQuery,
      statusCodes: effectiveCodes,
      pageNo: 1
    };
    dispatch(changeOffersQuery(newQuery));
    dispatch(getOffers(newQuery));
    dispatch(getOffersFilters({ search: newQuery.search }));
  };

  // Handle archive view toggle
  const handleArchiveViewChange = (view: "active" | "archived") => {
    setArchiveViewMode(view);
    setStripFilterCode(null); // Clear action strip filter
    if (view === "archived") {
      // Switch to archive: query only archived items
      const newQuery = {
        ...offersQuery,
        statusCodes: [OfferStatusCode.archived],
        pageNo: 1,
      };
      dispatch(changeOffersQuery(newQuery));
      dispatch(getOffers(newQuery));
      dispatch(getOffersFilters({ search: newQuery.search }));
    } else {
      // Switch to active: query all non-archived statuses
      const newQuery = {
        ...offersQuery,
        statusCodes: [...allActiveStatusCodes, ...allClosedStatusCodes],
        pageNo: 1,
      };
      dispatch(changeOffersQuery(newQuery));
      dispatch(getOffers(newQuery));
      dispatch(getOffersFilters({ search: newQuery.search }));
    }
  };

  const handleFilterToggle = useCallback(
    (statusCode: number) => {
      // If in archive view and user clicks a status filter, switch to active view
      if (archiveViewMode === "archived") {
        setArchiveViewMode("active");
        // Start with the clicked status code selected
        const newQuery = {
          ...offersQuery,
          statusCodes: [statusCode],
          pageNo: 1,
        };
        dispatch(changeOffersQuery(newQuery));
        dispatch(getOffers(newQuery));
        dispatch(getOffersFilters({ search: newQuery.search }));
        return;
      }
      const current = offersQuery.statusCodes || [];
      const next = current.includes(statusCode)
        ? current.filter((c: number) => c !== statusCode)
        : [...current, statusCode];
      handleFiltersChange(next);
    },
    [offersQuery.statusCodes, handleFiltersChange, archiveViewMode]
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.trim().length === 0) {
        handleSearch(undefined);
        return;
      }
      debounceRef.current = setTimeout(() => {
        handleSearch(value || undefined);
      }, 400);
    },
    [handleSearch]
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        handleSearch(searchValue || undefined);
      }
    },
    [searchValue, handleSearch]
  );

  const handleDateFromChange = (value: Dayjs | null) => {
    const newQuery = { ...offersQuery, dateFrom: value?.toISOString(), pageNo: 1 };
    dispatch(changeOffersQuery(newQuery));
    dispatch(getOffers(newQuery));
    dispatch(getOffersFilters({ search: newQuery.search }));
  };

  const handleDateToChange = (value: Dayjs | null) => {
    const newQuery = { ...offersQuery, dateTo: value?.toISOString(), pageNo: 1 };
    dispatch(changeOffersQuery(newQuery));
    dispatch(getOffers(newQuery));
    dispatch(getOffersFilters({ search: newQuery.search }));
  };

  const handleView = (offer: Offer) => {
    navigate(`/service-offers/${offer.id}`);
  };

  const handleEdit = (offer: Offer) => {
    navigate(`/service-offers/edit/${offer.id}`);
  };

  const handleSubmit = async (offer: Offer) => {
    await dispatch(submitOffer(offer.id));
    await dispatch(getOffers(offersQuery));
    await dispatch(getOffersFilters({ search: offersQuery.search }));
  };

  const handleWithdraw = (offer: Offer) => {
    confirm({
      title: translate("withdrawOfferTitle"),
      text: translate("withdrawOfferText", { number: offer.internalNumber }),
      confirmButton: translate("yesWithdraw"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: async () => {
        await dispatch(withdrawOffer(offer.id));
        await dispatch(getOffers(offersQuery));
        await dispatch(getOffersFilters({ search: offersQuery.search }));
      }
    });
  };

  const handleDelete = (offer: Offer) => {
    confirm({
      title: translate("deleteOfferTitle"),
      text: translate("deleteOfferText", { number: offer.internalNumber }),
      confirmButton: translate("yesDelete"),
      closeButton: translate("common:common.no"),
      severity: "error",
      onConfirm: async () => {
        await dispatch(deleteOffer(offer.id));
        await dispatch(getOffers(offersQuery));
        await dispatch(getOffersFilters({ search: offersQuery.search }));
      }
    });
  };

  const handleOpenConfirmDialog = (offer: Offer) => {
    setConfirmingOffer(offer);
    setConfirmDialogOpen(true);
  };

  const handleConfirmService = async (data: {
    arrivalDate: string;
    arrivalTimeFrom: string;
    arrivalTimeTo: string;
    message?: string;
  }) => {
    if (!confirmingOffer) return;
    setConfirmDialogOpen(false);
    await dispatch(confirmOffer({ offerId: confirmingOffer.id, data }));
    await dispatch(getOffers(offersQuery));
    await dispatch(getOffersFilters({ search: offersQuery.search }));
    setConfirmingOffer(null);
  };

  // R2: Inline action handlers for execution states
  const handleStartWork = (offer: Offer) => {
    confirm({
      title: translate("execution.startWorkTitle"),
      text: translate("execution.startWorkText", { number: offer.internalNumber }),
      confirmButton: translate("execution.yesStartWork"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: async () => {
        await dispatch(startWork(offer.id));
        await dispatch(getOffers(offersQuery));
        await dispatch(getOffersFilters({ search: offersQuery.search }));
      },
    });
  };

  const handleMarkComplete = (offer: Offer) => {
    setCompletingOffer(offer);
    setCompleteDialogOpen(true);
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Send the full data URI (e.g. "data:application/pdf;base64,...")
        // AzureBlobStorage expects the full data URI, not stripped base64
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleCompleteWork = async (data: {
    completionSummary: string;
    providerNotes?: string;
    serviceReportFiles?: File[];
  }) => {
    if (!completingOffer) return;
    setCompleteLoading(true);
    try {
      // Convert File objects to base64 for the API
      let files: Array<{ name: string; content: string }> | undefined;
      if (data.serviceReportFiles && data.serviceReportFiles.length > 0) {
        files = await Promise.all(
          data.serviceReportFiles.map(async (f) => ({
            name: f.name,
            content: await fileToBase64(f),
          }))
        );
      }

      const result = await dispatch(markWorkComplete({
        offerId: completingOffer.id,
        data: {
          completionSummary: data.completionSummary,
          providerNotes: data.providerNotes,
          files,
        },
      }));
      if (markWorkComplete.rejected.match(result)) {
        // API call failed — keep dialog open so user can retry
        console.error("Mark work complete failed:", result.error);
        return;
      }
      setCompleteDialogOpen(false);
      setCompletingOffer(null);
      await dispatch(getOffers(offersQuery));
      await dispatch(getOffersFilters({ search: offersQuery.search }));
    } finally {
      setCompleteLoading(false);
    }
  };

  const handleReview = (offer: Offer) => {
    navigate(`/service-offers/${offer.id}`);
  };

  const handleArchive = async (offer: Offer) => {
    await dispatch(archiveOffer(offer.id));
    await dispatch(getOffers(offersQuery));
    await dispatch(getOffersFilters({ search: offersQuery.search }));
  };

  const handleUnarchive = async (offer: Offer) => {
    await dispatch(unarchiveOffer(offer.id));
    await dispatch(getOffers(offersQuery));
    await dispatch(getOffersFilters({ search: offersQuery.search }));
  };

  // R6: Track which strip badge is active (null = "All" / no strip filter)
  const [stripFilterCode, setStripFilterCode] = useState<OfferStatusCode | null>(null);

  // R6: Handle badge click — toggle: click active filter deselects it
  const handleStripBadgeClick = (statusCode: OfferStatusCode) => {
    // If in archive view, switch back to active first
    if (archiveViewMode === "archived") {
      setArchiveViewMode("active");
    }
    if (stripFilterCode === statusCode) {
      // Already active — deselect (show all non-archived)
      setStripFilterCode(null);
      const newQuery = {
        ...offersQuery,
        statusCodes: [...allActiveStatusCodes, ...allClosedStatusCodes],
        pageNo: 1,
      };
      dispatch(changeOffersQuery(newQuery));
      dispatch(getOffers(newQuery));
      dispatch(getOffersFilters({ search: newQuery.search }));
    } else {
      setStripFilterCode(statusCode);
      const newQuery = {
        ...offersQuery,
        statusCodes: [statusCode],
        pageNo: 1,
      };
      dispatch(changeOffersQuery(newQuery));
      dispatch(getOffers(newQuery));
      dispatch(getOffersFilters({ search: newQuery.search }));
    }
  };

  // Handle "All" badge click — always clears filter
  const handleStripAllClick = () => {
    // If in archive view, switch back to active
    if (archiveViewMode === "archived") {
      setArchiveViewMode("active");
    }
    setStripFilterCode(null);
    const newQuery = {
      ...offersQuery,
      statusCodes: [...allActiveStatusCodes, ...allClosedStatusCodes],
      pageNo: 1,
    };
    dispatch(changeOffersQuery(newQuery));
    dispatch(getOffers(newQuery));
    dispatch(getOffersFilters({ search: newQuery.search }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-EU", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };


  if (loading && !offers) {
    return <Loader />;
  }

  const activeFilterCount = offersQuery.statusCodes?.length ?? 0;

  // ═══════════════════════════════════════════════════════════
  //  Shared: active group toggle handler
  // ═══════════════════════════════════════════════════════════

  const handleActiveGroupToggle = () => {
    // If in archive view, switch to active view
    if (archiveViewMode === "archived") {
      setArchiveViewMode("active");
      const activeCodes = activeFilters.map((f) => f.id);
      const newQuery = {
        ...offersQuery,
        statusCodes: activeCodes,
        pageNo: 1,
      };
      dispatch(changeOffersQuery(newQuery));
      dispatch(getOffers(newQuery));
      dispatch(getOffersFilters({ search: newQuery.search }));
      return;
    }
    const activeCodes = activeFilters.map((f) => f.id);
    const allSelected = activeCodes.every((c: number) =>
      (offersQuery.statusCodes || []).includes(c)
    );
    if (allSelected) {
      handleFiltersChange(
        (offersQuery.statusCodes || []).filter(
          (c: number) => !activeCodes.includes(c)
        )
      );
    } else {
      const combined = [...(offersQuery.statusCodes || [])];
      activeCodes.forEach((c: number) => {
        if (!combined.includes(c)) combined.push(c);
      });
      handleFiltersChange(combined);
    }
  };

  const handleClosedGroupToggle = () => {
    // If in archive view, switch to active view
    if (archiveViewMode === "archived") {
      setArchiveViewMode("active");
      const closedCodes = closedFilters.map((f) => f.id);
      const newQuery = {
        ...offersQuery,
        statusCodes: closedCodes,
        pageNo: 1,
      };
      dispatch(changeOffersQuery(newQuery));
      dispatch(getOffers(newQuery));
      dispatch(getOffersFilters({ search: newQuery.search }));
      return;
    }
    const closedCodes = closedFilters.map((f) => f.id);
    const allSelected = closedCodes.every((c: number) =>
      (offersQuery.statusCodes || []).includes(c)
    );
    if (allSelected) {
      handleFiltersChange(
        (offersQuery.statusCodes || []).filter(
          (c: number) => !closedCodes.includes(c)
        )
      );
    } else {
      const combined = [...(offersQuery.statusCodes || [])];
      closedCodes.forEach((c: number) => {
        if (!combined.includes(c)) combined.push(c);
      });
      handleFiltersChange(combined);
    }
  };

  // ═══════════════════════════════════════════════════════════
  //  Shared: inline filter content (used in sidebar & accordion)
  // ═══════════════════════════════════════════════════════════

  const renderFilterContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Box sx={{ flexShrink: 0 }}>
      {/* Search input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: dt.inputBg,
          borderRadius: dt.radiusSm,
          border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
          padding: "0 12px",
          height: "40px",
          transition: "all 0.25s ease",
          boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
          mb: "16px",
        }}
      >
        <SearchOutlined
          sx={{ fontSize: "16px", color: searchFocused ? dt.blue : dt.textMuted, flexShrink: 0, transition: "color 0.2s" }}
        />
        <input
          type="text"
          value={searchValue}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyDown}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder={translate("common:buttons.search") + "..."}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            fontSize: "13px",
            fontFamily: 'var(--font-sans)',
            color: dt.textPrimary,
            background: "transparent",
            marginLeft: "8px",
          }}
        />
      </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", "&::-webkit-scrollbar": { width: "5px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#D1D5DB", borderRadius: "10px", "&:hover": { background: "#9CA3AF" } }, scrollbarWidth: "thin" as const, scrollbarColor: "#D1D5DB transparent" }}>

      {/* Date Range */}
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1.5px",
          color: dt.textMuted,
          fontFamily: dt.font,
          mb: "10px",
          px: "4px",
        }}
      >
        {translate("dateRange")}
      </Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: "4px", mb: "4px" }}>
          <DatePicker
            label={translate("from")}
            value={offersQuery.dateFrom ? dayjs(offersQuery.dateFrom) : null}
            onChange={handleDateFromChange}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: dt.radiusSm,
                    fontFamily: dt.font,
                    fontSize: "13px",
                    backgroundColor: dt.inputBg,
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: dt.cardBorder },
                    "&.Mui-focused fieldset": { borderColor: dt.blue, borderWidth: "1.5px" },
                  },
                  "& .MuiInputLabel-root": { fontFamily: dt.font, fontSize: "13px" },
                },
              },
            }}
          />
          <DatePicker
            label={translate("to")}
            value={offersQuery.dateTo ? dayjs(offersQuery.dateTo) : null}
            onChange={handleDateToChange}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: dt.radiusSm,
                    fontFamily: dt.font,
                    fontSize: "13px",
                    backgroundColor: dt.inputBg,
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: dt.cardBorder },
                    "&.Mui-focused fieldset": { borderColor: dt.blue, borderWidth: "1.5px" },
                  },
                  "& .MuiInputLabel-root": { fontFamily: dt.font, fontSize: "13px" },
                },
              },
            }}
          />
        </Box>
      </LocalizationProvider>

      {/* Divider */}
      <Box sx={{ borderTop: `1px solid ${dt.cardBorder}`, mb: "16px" }} />

      {/* Filter header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px", px: "4px" }}>
        <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font }}>
          {translate("filterBy")}
        </Box>
        {((offersQuery.statusCodes?.length ?? 0) > 0 || offersQuery.dateFrom || offersQuery.dateTo) && (
          <Box
            onClick={() => {
              handleFiltersChange([]);
              handleDateFromChange(null);
              handleDateToChange(null);
            }}
            sx={{ fontSize: "12px", color: dt.blue, cursor: "pointer", fontWeight: 500, fontFamily: dt.font, "&:hover": { textDecoration: "underline" } }}
          >
            {translate("clearAll")}
          </Box>
        )}
      </Box>

      {/* Active offers group */}
      <RedesignFilterItem
        label={translate("activeOffers")}
        count={activeTotal}
        isSelected={activeFilters.length > 0 && activeFilters.every((f) => (offersQuery.statusCodes || []).includes(f.id))}
        onClick={handleActiveGroupToggle}
      />

      {/* Individual active status filters */}
      {activeFilters.map((filter) => (
        <RedesignFilterItem
          key={filter.id}
          label={statusLabelMap[filter.id]}
          count={filter.value}
          isSelected={(offersQuery.statusCodes || []).includes(filter.id)}
          statusColor={statusBadgeColors[filter.id]?.text}
          indent
          onClick={() => handleFilterToggle(filter.id)}
        />
      ))}

      {/* Separator */}
      <Box sx={{ borderTop: `1px dashed ${dt.cardBorder}`, my: "8px", mx: "4px" }} />

      {/* Closed offers group */}
      <RedesignFilterItem
        label={translate("closedOffers")}
        count={closedTotal}
        isSelected={closedFilters.length > 0 && closedFilters.every((f) => (offersQuery.statusCodes || []).includes(f.id))}
        dimmed
        onClick={handleClosedGroupToggle}
      />

      {/* Individual closed status filters */}
      {closedFilters.map((filter) => (
        <RedesignFilterItem
          key={filter.id}
          label={statusLabelMap[filter.id]}
          count={filter.value}
          isSelected={(offersQuery.statusCodes || []).includes(filter.id)}
          statusColor={statusBadgeColors[filter.id]?.text}
          indent
          dimmed
          onClick={() => handleFilterToggle(filter.id)}
        />
      ))}
      </Box>
    </Box>
  );

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        overflowX: "hidden",
        px: { xs: "16px", md: "32px" },
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      <ActionToast
        open={!!error}
        variant="error"
        message={translate(NotificationMessage.error(error).message)}
        onClose={() => dispatch(clearError())}
        duration={6000}
      />

      {/* ═══════════════════════════════════════════════════════
          MAIN CONTENT
          ═══════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          pt: { xs: 3, md: 4 },
          pb: 3,
          minWidth: 0,
          height: "calc(100vh - 160px)",
          alignItems: "stretch",
          overflow: "hidden",
        }}
      >
        {/* ═════════════════════════════════════════════════════
            DESKTOP SIDEBAR (>1200px only)
            ═════════════════════════════════════════════════════ */}
        {!isCompact && (
          <Box
            sx={{
              width: "260px",
              minWidth: "260px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                padding: "22px",
                boxShadow: dt.cardShadow,
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {renderFilterContent()}
            </Box>
          </Box>
        )}

        {/* ═════════════════════════════════════════════════════
            MAIN AREA (table or cards)
            ═════════════════════════════════════════════════════ */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: "16px",
            overflow: "hidden",
            animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
          }}
        >
          {/* ═══════════════════════════════════════════════════
              R6: ACTION REQUIRED STRIP + VIEW TOGGLE
              ═══════════════════════════════════════════════════ */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {archiveViewMode === "active" ? (
                <ActionRequiredStrip
                  offersFilters={offersFilters}
                  activeFilterCode={stripFilterCode}
                  translate={translate}
                  onBadgeClick={handleStripBadgeClick}
                  onAllClick={handleStripAllClick}
                />
              ) : (
                <Box sx={{ fontSize: "14px", fontWeight: 600, color: dt.textSecondary, fontFamily: dt.font }}>
                  {translate("archivedView")}
                </Box>
              )}
            </Box>
            <ArchiveViewToggle
              activeView={archiveViewMode}
              activeCount={allNonArchivedTotal}
              archivedCount={archiveFilter.value}
              onViewChange={handleArchiveViewChange}
              activeLabel={translate("activeView")}
              archivedLabel={translate("archivedView")}
            />
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, val) => val && setViewMode(val)}
              size="small"
              sx={{
                flexShrink: 0,
                "& .MuiToggleButton-root": {
                  border: `1px solid ${dt.cardBorder}`,
                  borderRadius: `${dt.radiusSm} !important`,
                  padding: "4px 10px",
                  textTransform: "none",
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: dt.textSecondary,
                  transition: "all 0.2s ease",
                  "&.Mui-selected": {
                    backgroundColor: dt.blueGlow,
                    color: dt.blue,
                    borderColor: dt.blue,
                    "&:hover": { backgroundColor: dt.blueGlow },
                  },
                  "&:hover": { backgroundColor: dt.blueSoft },
                },
              }}
            >
              <ToggleButton value="cards">
                <CardViewIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                {translate("viewToggle.cards")}
              </ToggleButton>
              <ToggleButton value="compact">
                <CompactViewIcon sx={{ fontSize: "16px", mr: 0.5 }} />
                {translate("viewToggle.compact")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* ═══════════════════════════════════════════════════
              ACCORDION FILTERS (tablet & phone — compact mode)
              ═══════════════════════════════════════════════════ */}
          {isCompact && (
            <Accordion
              expanded={filtersExpanded}
              onChange={() => setFiltersExpanded(!filtersExpanded)}
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: `${dt.radiusSm} !important`,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: "none !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 !important" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: dt.textSecondary }} />}
                sx={{
                  minHeight: "48px",
                  px: 2,
                  "&.Mui-expanded": { minHeight: "48px" },
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    gap: 1,
                    my: "10px",
                    "&.Mui-expanded": { my: "10px" },
                  },
                }}
              >
                <FilterListIcon sx={{ color: dt.textMuted, fontSize: "20px" }} />
                <Box sx={{ fontSize: "14px", fontWeight: 500, color: dt.textSecondary, fontFamily: dt.font }}>
                  {translate("filterBy")}
                </Box>
                {activeFilterCount > 0 && (
                  <Badge
                    badgeContent={activeFilterCount}
                    sx={{
                      ml: 1,
                      "& .MuiBadge-badge": {
                        backgroundColor: dt.blue,
                        color: "#fff",
                        fontSize: "10px",
                        fontFamily: dt.fontMono,
                        minWidth: "18px",
                        height: "18px",
                      },
                    }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {renderFilterContent()}
              </AccordionDetails>
            </Accordion>
          )}

          {/* ═══════════════════════════════════════════════════
              CARD VIEW (cards mode — all screen sizes)
              ═══════════════════════════════════════════════════ */}
          {viewMode === "cards" && (
            <>
              {(offers?.items?.length ?? 0) > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                    borderRadius: dt.radiusSm,
                    backgroundColor: dt.cardBg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: { xs: 1, sm: 1.5, md: 2 },
                      "&::-webkit-scrollbar": { width: "5px" },
                      "&::-webkit-scrollbar-track": { background: "transparent" },
                      "&::-webkit-scrollbar-thumb": { background: "#D1D5DB", borderRadius: "10px", "&:hover": { background: "#9CA3AF" } },
                      scrollbarWidth: "thin" as const,
                      scrollbarColor: "#D1D5DB transparent",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        width: "100%",
                      }}
                    >
                      {offers?.items?.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={changedIds.has(item.id) ? { animation: "rowHighlight 1.5s ease-out", borderRadius: "8px" } : undefined}
                        >
                          <OfferCard
                            item={item}
                            index={index}
                            formatCurrency={formatCurrency}
                            onClick={handleCardClick}
                            onConfirmService={handleOpenConfirmDialog}
                            onStartWork={handleStartWork}
                            onMarkComplete={handleMarkComplete}
                            onReview={handleReview}
                            onSubmit={handleSubmit}
                            onWithdraw={handleWithdraw}
                            onArchive={handleArchive}
                            onUnarchive={handleUnarchive}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      borderTop: `1px solid ${dt.cardBorder}`,
                      px: { xs: 1, md: 2 },
                      ...paginationWrapperSx,
                    }}
                  >
                    <LightTablePagination
                      totalCount={offers?.totalCount ?? 0}
                      currentPage={offersQuery.pageNo ?? 1}
                      pageSize={offersQuery.pageSize ?? 10}
                      pageSizeOptions={[10, 25, 50, 100]}
                      onPageChange={handlePageNoChange}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageLabel={translate("rowsPerPage")}
                      ofLabel={translate("of")}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radiusLg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    minHeight: "300px",
                    flex: 1,
                  }}
                >
                  <TableEmpty
                    iconComponent={BoxSearchIcon}
                    text={translate("noOffersFound")}
                    secondaryText={translate("startSendingOffers")}
                  />
                </Box>
              )}
            </>
          )}

          {/* ═══════════════════════════════════════════════════
              COMPACT CARD VIEW (compact mode — all screen sizes)
              ═══════════════════════════════════════════════════ */}
          {viewMode === "compact" && (
            <>
              {(offers?.items?.length ?? 0) > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                    borderRadius: dt.radiusSm,
                    backgroundColor: dt.cardBg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: { xs: 1, sm: 1.5, md: 2 },
                      "&::-webkit-scrollbar": { width: "5px" },
                      "&::-webkit-scrollbar-track": { background: "transparent" },
                      "&::-webkit-scrollbar-thumb": { background: "#D1D5DB", borderRadius: "10px", "&:hover": { background: "#9CA3AF" } },
                      scrollbarWidth: "thin" as const,
                      scrollbarColor: "#D1D5DB transparent",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        width: "100%",
                      }}
                    >
                      {offers?.items?.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={changedIds.has(item.id) ? { animation: "rowHighlight 1.5s ease-out", borderRadius: "8px" } : undefined}
                        >
                          <OfferCardCompact
                            item={item}
                            index={index}
                            formatCurrency={formatCurrency}
                            onClick={handleCardClick}
                            onConfirmService={handleOpenConfirmDialog}
                            onStartWork={handleStartWork}
                            onMarkComplete={handleMarkComplete}
                            onReview={handleReview}
                            onSubmit={handleSubmit}
                            onWithdraw={handleWithdraw}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      borderTop: `1px solid ${dt.cardBorder}`,
                      px: { xs: 1, md: 2 },
                      ...paginationWrapperSx,
                    }}
                  >
                    <LightTablePagination
                      totalCount={offers?.totalCount ?? 0}
                      currentPage={offersQuery.pageNo ?? 1}
                      pageSize={offersQuery.pageSize ?? 10}
                      pageSizeOptions={[10, 25, 50, 100]}
                      onPageChange={handlePageNoChange}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageLabel={translate("rowsPerPage")}
                      ofLabel={translate("of")}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radiusLg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    minHeight: "300px",
                    flex: 1,
                  }}
                >
                  <TableEmpty
                    iconComponent={BoxSearchIcon}
                    text={translate("noOffersFound")}
                    secondaryText={translate("startSendingOffers")}
                  />
                </Box>
              )}
            </>
          )}

        </Box>
      </Box>

      <ConfirmServiceDialog
        open={confirmDialogOpen}
        offerNumber={confirmingOffer?.internalNumber || ""}
        defaultArrivalDate={confirmingOffer?.earliestStartDate}
        onConfirm={handleConfirmService}
        onClose={() => {
          setConfirmDialogOpen(false);
          setConfirmingOffer(null);
        }}
      />

      <CompleteWorkDialog
        open={completeDialogOpen}
        offerNumber={completingOffer?.internalNumber || ""}
        onComplete={handleCompleteWork}
        onClose={() => {
          setCompleteDialogOpen(false);
          setCompletingOffer(null);
        }}
        loading={completeLoading}
      />

    </Box>
  );
};

export default MyOffers;
